import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportHistory = ({ history = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date'); // date, name, size, downloads

  const formatDateTime = (dateString) => {
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (sizeString) => {
    return sizeString;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'failed': return 'text-error';
      case 'processing': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'failed': return 'XCircle';
      case 'processing': return 'Clock';
      default: return 'Circle';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'package': return 'Package';
      case 'management': return 'BarChart3';
      case 'forecast': return 'TrendingUp';
      case 'compliance': return 'Shield';
      case 'audit': return 'Search';
      default: return 'FileText';
    }
  };

  const getFilteredHistory = () => {
    let filtered = history;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered?.filter(report =>
        report?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        report?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered?.filter(report => report?.type?.toLowerCase() === filterType);
    }

    // Sort reports
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'size':
          const aSize = parseFloat(a?.size);
          const bSize = parseFloat(b?.size);
          return bSize - aSize;
        case 'downloads':
          return b?.downloads - a?.downloads;
        default:
          return new Date(b.generatedAt) - new Date(a.generatedAt);
      }
    });

    return filtered;
  };

  const handleDownload = (reportId) => {
    console.log(`Downloading report: ${reportId}`);
    // Implement download functionality
  };

  const handleShare = (reportId) => {
    console.log(`Sharing report: ${reportId}`);
    // Implement share functionality
  };

  const handleDelete = (reportId) => {
    console.log(`Deleting report: ${reportId}`);
    // Implement delete functionality
  };

  const handleRegenerateReport = (reportId) => {
    console.log(`Regenerating report: ${reportId}`);
    // Navigate to generator with same configuration
  };

  const filteredHistory = getFilteredHistory();
  const totalReports = history?.length;
  const completedReports = history?.filter(r => r?.status === 'completed')?.length;
  const totalDownloads = history?.reduce((sum, r) => sum + r?.downloads, 0);
  const totalSize = history?.reduce((sum, r) => sum + parseFloat(r?.size), 0);

  const reportTypes = [...new Set(history.map(r => r.type.toLowerCase()))];

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalReports}</div>
              <div className="text-sm text-muted-foreground">Total Reports</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{completedReports}</div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Download" size={20} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalDownloads}</div>
              <div className="text-sm text-muted-foreground">Downloads</div>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="HardDrive" size={20} className="text-error" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalSize?.toFixed(1)}MB</div>
              <div className="text-sm text-muted-foreground">Total Size</div>
            </div>
          </div>
        </div>
      </div>
      {/* Search and Filter Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1 md:w-96">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Types</option>
            {reportTypes?.map((type) => (
              <option key={type} value={type}>
                {type?.charAt(0)?.toUpperCase() + type?.slice(1)}
              </option>
            ))}
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="size">Sort by Size</option>
            <option value="downloads">Sort by Downloads</option>
          </select>

          <Button variant="outline" iconName="Trash2" size="sm">
            Clear History
          </Button>
        </div>
      </div>
      {/* Reports List */}
      <div className="bg-card border border-border rounded-lg card-shadow">
        {filteredHistory?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Reports Found</h4>
            <p className="text-muted-foreground">
              {searchTerm ? 'Try adjusting your search terms' : 'No reports in history yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredHistory?.map((report) => (
              <div key={report?.id} className="p-6 hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name={getTypeIcon(report?.type)} size={24} className="text-muted-foreground" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <h4 className="text-lg font-semibold text-foreground">{report?.name}</h4>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                          {report?.type}
                        </span>
                        <div className={`flex items-center space-x-1 text-sm ${getStatusColor(report?.status)}`}>
                          <Icon name={getStatusIcon(report?.status)} size={14} />
                          <span className="capitalize">{report?.status}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="Calendar" size={14} />
                          <span>{formatDateTime(report?.generatedAt)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="HardDrive" size={14} />
                          <span>{formatFileSize(report?.size)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Download" size={14} />
                          <span>{report?.downloads} downloads</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="Download"
                      onClick={() => handleDownload(report?.id)}
                    >
                      Download
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="Share"
                      onClick={() => handleShare(report?.id)}
                    >
                      Share
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="RefreshCw"
                      onClick={() => handleRegenerateReport(report?.id)}
                    >
                      Regenerate
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="Trash2"
                      onClick={() => handleDelete(report?.id)}
                      className="text-error hover:text-error"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportHistory;